import { useApolloClient } from '@apollo/client/react';
import {
  GET_PROJECT_INITIATION_DATA_BY_RAW_PROJECT_ID,
  ProjectData,
  useNewProjectService,
} from 'domain/shared/newProject';
import { useAccountResourceService } from 'domain/shared/account';
import { usePresaleOutputService } from 'domain/shared/presale';

type ProjectInitiationGraphqlResponse = {
  baseProject?: ProjectData;
};

type ProjectInitiationInputDataParams = {
  accountId?: string;
  opportunityId?: string;
  rawProjectId?: string;
};

export const useProjectInitiationInputData = () => {
  const client = useApolloClient();
  const { getLastPresaleOutput, getOpportunityById } = usePresaleOutputService();
  const { getById } = useNewProjectService();
  const { getById: getAccountById } = useAccountResourceService();

  const getProjectByRawProjectId = async (rawProjectId: string) => {
    const response = await client.query<ProjectInitiationGraphqlResponse>({
      query: GET_PROJECT_INITIATION_DATA_BY_RAW_PROJECT_ID,
      variables: { rawProjectId },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    });

    return response.data?.baseProject;
  };

  const getExistingProject = async (relatedProjectId?: Nullable<string | number>) => {
    if (!relatedProjectId) {
      return null;
    }

    return getById(String(relatedProjectId));
  };

  const getByOpportunity = async (
    accountId: string,
    opportunityId: string,
    rawProjectId?: string,
    rawProject?: ProjectData,
  ) => {
    const [account, presaleOutput, opportunity] = await Promise.all([
      getAccountById(accountId),
      getLastPresaleOutput(opportunityId),
      getOpportunityById(opportunityId),
    ]);

    const existingProject = await getExistingProject(opportunity.relatedProject);

    if (existingProject) {
      opportunity.relatedProject = existingProject.id;
    }

    return {
      account,
      presaleOutput: {
        ...presaleOutput,
        requiredResources: rawProject?.team?.expectedStructure || presaleOutput?.requiredResources,
      },
      opportunity: {
        ...opportunity,
        rawProjectId,
      },
      relatedProject: existingProject,
      inputData: {
        accountId,
        opportunityId,
        rawProjectId,
      },
    };
  };

  const getProjectInitiationInputData = ({
    accountId,
    opportunityId,
    rawProjectId,
  }: ProjectInitiationInputDataParams) => {
    if (rawProjectId) {
      return getProjectByRawProjectId(rawProjectId).then(project => {
        const resolvedAccountId = accountId || project?.accountData?.accountId || project?.engagement?.accountId;
        const resolvedOpportunityId = opportunityId || project?.engagement?.crmId;

        if (!resolvedAccountId || !resolvedOpportunityId) {
          return Promise.reject(new Error('Account or opportunity is not specified'));
        }

        return getByOpportunity(String(resolvedAccountId), String(resolvedOpportunityId), rawProjectId, project);
      });
    }

    if (!accountId || !opportunityId) {
      return Promise.reject(new Error('Account or opportunity is not specified'));
    }

    return getByOpportunity(accountId, opportunityId);
  };

  return {
    getProjectInitiationInputData,
  };
};
